import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';

const RegistrationForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();

  // Check if APIs are available
  const areAPIsAvailable = () => {
    return typeof window !== 'undefined' &&
    window.ezsite &&
    window.ezsite.apis &&
    typeof window.ezsite.apis.register === 'function';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if APIs are available before attempting registration
    if (!areAPIsAvailable()) {
      setError('Registration system is loading. Please wait a moment and try again.');
      return;
    }

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password
      });
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4" data-id="aa9pab24r" data-path="src/components/auth/RegistrationForm.tsx">
      <Card className="w-full max-w-md shadow-xl border-0 bg-white/80 backdrop-blur-sm" data-id="xqgejg9sh" data-path="src/components/auth/RegistrationForm.tsx">
        <CardHeader className="space-y-1 text-center" data-id="vvkpwarln" data-path="src/components/auth/RegistrationForm.tsx">
          <CardTitle className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent" data-id="u8pergjjg" data-path="src/components/auth/RegistrationForm.tsx">
            Create Account
          </CardTitle>
          <CardDescription className="text-gray-600" data-id="piqyv1le8" data-path="src/components/auth/RegistrationForm.tsx">
            Join our educational platform today
          </CardDescription>
        </CardHeader>
        <CardContent data-id="pdydx76qf" data-path="src/components/auth/RegistrationForm.tsx">
          <form onSubmit={handleSubmit} className="space-y-4" data-id="ztqm1pzh5" data-path="src/components/auth/RegistrationForm.tsx">
            {!areAPIsAvailable() &&
            <Alert className="bg-yellow-50 border-yellow-200 mb-4" data-id="eot2llbia" data-path="src/components/auth/RegistrationForm.tsx">
                <AlertTriangle className="h-4 w-4 text-yellow-600" data-id="toyks8dcc" data-path="src/components/auth/RegistrationForm.tsx" />
                <AlertDescription className="text-yellow-800" data-id="j9qaonkzp" data-path="src/components/auth/RegistrationForm.tsx">
                  Registration system is loading. Please wait a moment before creating an account.
                  <Button
                  type="button"
                  variant="link"
                  onClick={handleRefreshPage}
                  className="p-0 h-auto ml-1 text-yellow-800 underline" data-id="qisyg0k9m" data-path="src/components/auth/RegistrationForm.tsx">
                    Refresh page
                  </Button>
                </AlertDescription>
              </Alert>
            }

            {error &&
            <Alert variant="destructive" className="mb-4" data-id="r1q9x54em" data-path="src/components/auth/RegistrationForm.tsx">
                <AlertDescription data-id="jzfce8iz7" data-path="src/components/auth/RegistrationForm.tsx">{error}</AlertDescription>
              </Alert>
            }
            
            <div className="space-y-2" data-id="tehwk5wvk" data-path="src/components/auth/RegistrationForm.tsx">
              <Label htmlFor="name" className="text-sm font-medium text-gray-700" data-id="vxt699lyo" data-path="src/components/auth/RegistrationForm.tsx">
                Full Name
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
                className="transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="1dxbtoswa" data-path="src/components/auth/RegistrationForm.tsx" />

            </div>

            <div className="space-y-2" data-id="k9izo0iof" data-path="src/components/auth/RegistrationForm.tsx">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700" data-id="3mkxrw9mm" data-path="src/components/auth/RegistrationForm.tsx">
                Email Address
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
                className="transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="cxrksgh9j" data-path="src/components/auth/RegistrationForm.tsx" />

            </div>

            <div className="space-y-2" data-id="9fqo7po5b" data-path="src/components/auth/RegistrationForm.tsx">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700" data-id="usxtbkc3k" data-path="src/components/auth/RegistrationForm.tsx">
                Password
              </Label>
              <div className="relative" data-id="33ijwzcmd" data-path="src/components/auth/RegistrationForm.tsx">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  required
                  className="pr-10 transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="bcfhqjoke" data-path="src/components/auth/RegistrationForm.tsx" />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="k8crewbap" data-path="src/components/auth/RegistrationForm.tsx">

                  {showPassword ? <EyeOff size={18} data-id="hg3a85fzf" data-path="src/components/auth/RegistrationForm.tsx" /> : <Eye size={18} data-id="p1ecg8p4h" data-path="src/components/auth/RegistrationForm.tsx" />}
                </button>
              </div>
            </div>

            <div className="space-y-2" data-id="xoh2c0tky" data-path="src/components/auth/RegistrationForm.tsx">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700" data-id="759mjii68" data-path="src/components/auth/RegistrationForm.tsx">
                Confirm Password
              </Label>
              <div className="relative" data-id="cybnjfd82" data-path="src/components/auth/RegistrationForm.tsx">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  required
                  className="pr-10 transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="9mpbmaynp" data-path="src/components/auth/RegistrationForm.tsx" />

                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="jhw0331bq" data-path="src/components/auth/RegistrationForm.tsx">

                  {showConfirmPassword ? <EyeOff size={18} data-id="jcvki0uek" data-path="src/components/auth/RegistrationForm.tsx" /> : <Eye size={18} data-id="txhxz8dem" data-path="src/components/auth/RegistrationForm.tsx" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium py-2.5 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              disabled={isLoading || !areAPIsAvailable()} data-id="r0h880dj6" data-path="src/components/auth/RegistrationForm.tsx">

              {isLoading ?
              <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" data-id="p087gyukn" data-path="src/components/auth/RegistrationForm.tsx" />
                  Creating Account...
                </> :

              'Create Account'
              }
            </Button>
          </form>

          <div className="mt-6 text-center" data-id="f48vy9rwd" data-path="src/components/auth/RegistrationForm.tsx">
            <p className="text-sm text-gray-600" data-id="eeqw7prcy" data-path="src/components/auth/RegistrationForm.tsx">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-200" data-id="iae0p5wzc" data-path="src/components/auth/RegistrationForm.tsx">

                Sign in here
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>);

};

export default RegistrationForm;